import React, { useState, useEffect } from "react";
import { Check, Bell } from "lucide-react";
import { DetailContainer, BackButton } from "./UI";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { getCategoryById } from "../services/CategoryModel";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import { sendEventInvitation } from "../services/meetlyService";
import { getCurrentUserId } from "../services/userService";
import { useUser } from "../context/user";
import styles from "./InviteUser.module.css";

// Full-screen "invite someone to this event" flow. Ranks every other user
// in the app by how many interests they share with the current user, then
// lets the current user send Firestore invitation documents from a
// swipeable card stack.
export default function InviteUsersScreen({
  event,
  userInterests = ["tech", "coffee", "music"],
  onBack,
  onInviteUser,
  invitedUsers = [],
}) {
  const { userData } = useUser();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Load compatible users with robust fallback filtering
  // Note: this fetches the ENTIRE "users" collection and filters/scores
  // client-side rather than querying — fine for a small user base, but
  // worth moving to a server-side/paginated approach if the user count
  // grows large.
  useEffect(() => {
    async function fetchCandidates() {
      setLoading(true);
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const eventAttendees = event.attendees || [];
        const myUid = getCurrentUserId(userData);

        // Normalize user interests to lowercase
        const rawMyInterests =
          userData?.interests || userData?.interest || userInterests;
        const myInterests = Array.isArray(rawMyInterests)
          ? rawMyInterests.map((i) => String(i).toLowerCase().trim())
          : ["tech", "coffee", "music"];

        const list = [];
        usersSnapshot.forEach((userDoc) => {
          const uId = userDoc.id;
          const uData = userDoc.data();

          // Exclude current user and people already attending this event
          if (uId !== myUid && !eventAttendees.includes(uId)) {
            const rawUInterests = uData.interests ||
              uData.interest || ["tech", "music"];
            const uInterests = Array.isArray(rawUInterests)
              ? rawUInterests.map((i) => String(i).toLowerCase().trim())
              : [];

            const shared = uInterests.filter((tag) =>
              myInterests.includes(tag)
            );

            list.push({
              id: uId,
              ...uData,
              sharedInterests: shared,
            });
          }
        });

        // Sort candidates by the highest number of shared interests
        list.sort(
          (a, b) => b.sharedInterests.length - a.sharedInterests.length
        );

        setCandidates(list);
      } catch (error) {
        console.error("Error fetching invite candidates:", error);
        showToast("Couldn't load people to invite. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    fetchCandidates();
  }, [event, userData, userInterests]);

  // Handle sending invitation to Firestore
  const handleSendInvite = async (user, e) => {
    e?.stopPropagation?.();
    const targetId = String(user.id || user.uid);
    const myUid = getCurrentUserId(userData);

    if (!myUid) {
      console.error("Error: No active user session UID found.");
      return;
    }

    if (invitedUsers.includes(targetId)) return;

    try {
      await sendEventInvitation(
        { ...userData, name: userData?.name || auth.currentUser?.displayName },
        myUid,
        user,
        event
      );

      if (onInviteUser) {
        onInviteUser(user, e);
      }
    } catch (error) {
      console.error("Error sending invitation document:", error);
      showToast("Couldn't send the invitation. Please try again.");
    }
  };

  return (
    <div className={styles.overlay}>
      {toastMessage && (
        <div className={styles.toastNotification}>{toastMessage}</div>
      )}
      <div className={styles.topBar}>
        <BackButton onClick={onBack} />
      </div>

      {/* Screen Title */}
      <div className={styles.titleWrapper}>
        <h2 className={styles.title}>Invite someone compatible with you</h2>
      </div>

      {/* Cards Scroll Container */}
      <div className={styles.cardsScroll}>
        {loading ? (
          <p className={styles.statusMessage}>Finding compatible people...</p>
        ) : candidates.length === 0 ? (
          <p className={styles.statusMessage}>
            No other users found right now.
          </p>
        ) : (
          candidates.map((user) => {
            const targetId = String(user.id || user.uid);
            const isInvited = invitedUsers.includes(targetId);
            const firstName = user.name ? user.name.split(" ")[0] : "User";

            return (
              <div key={targetId} className={styles.candidateCard}>
                {/* Avatar Circular Wrapper */}
                <div className={styles.avatarCircle}>
                  <img
                    src={getAvatarUrl(user)}
                    alt={user.name || "User"}
                    className={styles.avatarImg}
                  />
                </div>

                {/* Name */}
                <h3 className={styles.candidateName}>{user.name || "User"}</h3>

                {/* Bio */}
                <p className={styles.candidateBio}>
                  {user.bio ||
                    "Always up for exploring new places, attending cultural events, and catching up with friends. Looking forward to meeting people who enjoy good conversations."}
                </p>

                {/* Common interests heading */}
                <p className={styles.commonInterestsHeading}>
                  Common interests
                </p>

                {/* Shared Interests Badges */}
                <div className={styles.interestsBadgeRow}>
                  {user.sharedInterests && user.sharedInterests.length > 0 ? (
                    user.sharedInterests.map((interestId) => {
                      const sharedCat = getCategoryById(interestId);
                      return (
                        <span key={interestId} className={styles.interestBadge}>
                          {sharedCat.icon} {sharedCat.label}
                        </span>
                      );
                    })
                  ) : (
                    <span className={styles.interestBadgeFallback}>
                      General Activities
                    </span>
                  )}
                </div>

                {/* Action Button */}
                <button
                  onClick={(e) => handleSendInvite(user, e)}
                  className={
                    isInvited
                      ? styles.inviteActionBtnSent
                      : styles.inviteActionBtnDefault
                  }
                >
                  {isInvited ? (
                    <>
                      <Check size={18} strokeWidth={3} /> Invitation Sent
                    </>
                  ) : (
                    <>
                      <Bell size={16} /> Make plans with {firstName} to go
                      together
                    </>
                  )}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
